import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { fetchAddressesAPI, addressToFakeContact } from '../../../api/addresses';
import { selectCustomerById } from '../../../entities/Customer/customerSelectors';
import { siteGroupSelector } from '../../../entities/SiteGroup/siteGroupSelectors';

/**
 * Hook for loading Address records and converting them to fake contacts
 *
 * Default Address Integration
 *
 * This hook fetches Address records from the backend and converts them to
 * Contact-like objects for use in AddressBookDialog. Address records represent
 * company default shipping/billing addresses (not person-specific).
 *
 * @param {Object} params
 * @param {number} params.customerId - Customer ID to fetch addresses for
 * @param {number} params.siteGroupId - Site Group ID to fetch addresses for (optional)
 * @param {boolean} params.primaryOnly - Only fetch primary (default) addresses
 * @param {string} params.addressType - Filter by type: 'shipping', 'billing', or 'all' (default: 'all')
 * @param {string} params.customerName - Optional customer name to use for display (avoids Redux lookup issues)
 * @param {string} params.siteGroupName - Optional site group name to use for display (avoids Redux lookup issues)
 * @returns {Object} { addresses, loading, error, refetch }
 */
export const useAddressFetch = ({
  customerId,
  siteGroupId,
  primaryOnly = true,
  addressType = 'all',
  customerName: providedCustomerName,
  siteGroupName: providedSiteGroupName,
}) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get customer and site group from Redux for display names
  const customer = useSelector(state => selectCustomerById(customerId)(state));
  const siteGroups = useSelector(siteGroupSelector);
  const siteGroup = siteGroups ? siteGroups.get(String(siteGroupId)) : null;

  const loadData = async () => {
    if (!customerId && !siteGroupId) {
      setAddresses([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const filters = {
        primary_only: primaryOnly,
      };

      // Filter by address type (is_billing: true = billing, false = shipping)
      if (addressType === 'billing') {
        filters.is_billing = true;
      } else if (addressType === 'shipping') {
        filters.is_billing = false;
      }
      // 'all' doesn't filter by is_billing

      if (siteGroupId) {
        // When site_group is provided, don't pass customer filter
        // The backend's include_customer_addresses will handle fetching both
        // site-group-level and customer-level addresses
        filters.site_group = siteGroupId;
        filters.include_customer_addresses = true;
      } else if (customerId) {
        // Only pass customer filter when no site_group is provided
        filters.customer = customerId;
      }

      const addressRecords = await fetchAddressesAPI(filters);

      // Use provided names if available, otherwise fall back to Redux lookup
      const customerName =
        providedCustomerName ||
        (customer?.get ? customer.get('name', 'Unknown Customer') : customer?.name || 'Unknown Customer');

      const fakeContacts = addressRecords.map(addr => {
        let businessName;
        let scope;

        if (addr.site_group != null) {
          // For site group addresses, use provided name if it matches the requested siteGroupId
          // Otherwise fall back to Redux lookup
          if (providedSiteGroupName && addr.site_group === siteGroupId) {
            businessName = providedSiteGroupName;
          } else {
            const addressSiteGroup = siteGroups ? siteGroups.get(String(addr.site_group)) : null;
            businessName = addressSiteGroup?.get
              ? addressSiteGroup.get('name', `Site Group #${addr.site_group}`)
              : addressSiteGroup?.name || `Site Group #${addr.site_group}`;
          }
          scope = 'Site Group';
        } else {
          businessName = customerName;
          scope = 'Customer';
        }

        const fakeContact = addressToFakeContact(addr, businessName);
        fakeContact._addressScope = scope;
        return fakeContact;
      });

      setAddresses(fakeContacts);
    } catch (err) {
      console.error('useAddressFetch: Failed to load addresses:', err);
      setError(err.message || 'Failed to load addresses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [customerId, siteGroupId, primaryOnly, addressType]);

  return {
    addresses,
    loading,
    error,
    refetch: loadData,
  };
};
